"use client";

// Fiche d'un tour reserve a un client B2B (GT-261007-HVGQ).
//
// La page tour est rendue cote serveur SANS compte : pour un tour reserve, le backend y
// repond 404 `private_tour`, et page.tsx rend cette vue au lieu de la fiche. Elle relit le
// tour depuis le navigateur avec le jeton du client (`api`), avec la ville et les continents
// du fil d'Ariane, puis rend EXACTEMENT la meme page que pour un tour public (TourPageView) :
// pour ceux qui peuvent le reserver, un tour reserve est un tour comme un autre. Pour tout
// autre compte, ou sans compte, il reste introuvable.
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { Route } from "next";
import { useTranslation } from "react-i18next";
import api from "@/app/lib/reducers/api";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { setGalleries, setTourDetails } from "@/app/lib/reducers/tourReducer";
import { Gallery, TourDetails } from "@/data/types";
import TourPageView from "./TourPageView";

interface Props {
  lang: string;
  currency: string;
  citySlug: string;
  tourSlug: string;
}

type State =
  | { status: "loading" }
  | { status: "denied" }
  | { status: "ready"; tour: TourDetails; galleries: Gallery[]; city: any; continents: any[] };

const publicJson = async (path: string) => {
  try {
    const res = await fetch(`${process.env.NEXT_PUBLIC_SOCKET}${path}`, { headers: { Accept: "application/json" } });
    return res.ok ? await res.json() : null;
  } catch {
    return null;
  }
};

const PrivateTourView: React.FC<Props> = ({ lang, currency, citySlug, tourSlug }) => {
  const { t } = useTranslation("guydeez");
  const dispatch = useAppDispatch();
  const user = useAppSelector((s: any) => s.auth?.user);
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    let alive = true;
    const hasSession = typeof window !== "undefined" && Boolean(localStorage.getItem("access_token"));
    if (!hasSession) {
      setState({ status: "denied" });
      return undefined;
    }
    setState({ status: "loading" });
    (async () => {
      try {
        // Un appel authentifie d'abord : un jeton d'acces expire y est rafraichi
        // (intercepteur `api`). La fiche, elle, accepte un jeton expire en visiteur
        // et repondrait « introuvable ».
        await api.get("/api/me/private-tours", { params: { language: lang } }).catch(() => null);
        const [tourRes, galleryRes, city, continentsResp] = await Promise.all([
          api.get(`/api/tour/${tourSlug}`, { params: { language: lang } }),
          api.get(`/api/tour/${tourSlug}/get-gallery`).catch(() => null),
          publicJson(`/api/city/data?slug=${encodeURIComponent(citySlug)}&language=${encodeURIComponent(lang)}`),
          publicJson(`/api/continents?activated=true&language=${encodeURIComponent(lang)}`),
        ]);
        if (!alive) return;
        const tour = tourRes.data as TourDetails;
        const galleries: Gallery[] = galleryRes?.data?.galleries || [];
        const continents = Array.isArray(continentsResp) ? continentsResp : continentsResp?.continents || [];
        // Les ilots de la fiche (formulaire, galerie, description) lisent le store.
        dispatch(setTourDetails(tour));
        dispatch(setGalleries(galleries));
        setState({ status: "ready", tour, galleries, city, continents });
      } catch {
        if (alive) setState({ status: "denied" });
      }
    })();
    return () => {
      alive = false;
    };
    // Relu quand le compte change (connexion depuis cette page).
  }, [tourSlug, citySlug, lang, dispatch, user?.uuid]);

  if (state.status === "loading") {
    // Squelette de la fiche : meme gabarit que la page qui va s'afficher.
    return (
      <div className="gdx animate-pulse" aria-busy="true" aria-label={t("private_tour_loading", { defaultValue: "Chargement de votre offre…" })}>
        <div style={{ height: 14, width: 260, borderRadius: 7, background: "var(--g-ink-100)", marginBottom: 18 }} />
        <div
          className="grid gap-1.5 grid-cols-1 md:grid-cols-[2fr_1fr_1fr] auto-rows-[280px] md:auto-rows-[230px]"
          style={{ borderRadius: 18, overflow: "hidden", marginBottom: 32 }}
        >
          <div className="md:row-span-2" style={{ background: "var(--g-cream-deep)" }} />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="hidden md:block" style={{ background: "var(--g-cream)" }} />
          ))}
        </div>
        <div className="grid gap-8 lg:gap-12 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px]">
          <div>
            <div style={{ height: 36, width: "70%", borderRadius: 10, background: "var(--g-ink-100)", marginBottom: 16 }} />
            {[100, 96, 88, 60].map((w) => (
              <div key={w} style={{ height: 12, width: `${w}%`, borderRadius: 6, background: "var(--g-ink-100)", marginBottom: 10 }} />
            ))}
          </div>
          <div className="hidden lg:block" style={{ height: 320, borderRadius: 22, background: "var(--g-cream)" }} />
        </div>
      </div>
    );
  }

  if (state.status === "denied") {
    // Visiteur : se connecter, puis revenir sur cette fiche. Compte connecte sans acces :
    // le tour a ete fait pour une autre agence — on l'oriente vers SON offre.
    const loggedIn = Boolean(user);
    const next = typeof window !== "undefined" ? window.location.pathname : "";
    return (
      <div className="gdx" style={{ maxWidth: 680, margin: "clamp(24px, 6vw, 64px) auto", padding: "0 4px" }}>
        <div
          className="gdx-card shadow"
          style={{
            position: "relative",
            overflow: "hidden",
            textAlign: "center",
            padding: "clamp(32px, 6vw, 56px) clamp(20px, 5vw, 48px) clamp(28px, 5vw, 40px)",
            background: "linear-gradient(180deg, var(--g-cream) 0%, #FFFFFF 62%)",
          }}
        >
          {/* Halo decoratif, aux couleurs de la marque */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: -90,
              left: "50%",
              transform: "translateX(-50%)",
              width: 320,
              height: 180,
              borderRadius: "50%",
              background: "radial-gradient(closest-side, rgba(79,191,110,.22), rgba(79,191,110,0))",
              pointerEvents: "none",
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: "relative",
              width: 76,
              height: 76,
              margin: "0 auto 18px",
              borderRadius: "50%",
              background: "#FFFFFF",
              border: "1px solid var(--g-cream-deep)",
              boxShadow: "var(--g-shadow-md)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--g-burgundy)",
            }}
          >
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="11" width="16" height="10" rx="2.5" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
              <circle cx="12" cy="16" r="1.4" fill="currentColor" stroke="none" />
            </svg>
          </div>
          <span className="gdx-pill cream" style={{ position: "relative", textTransform: "uppercase", letterSpacing: ".06em", fontSize: 11, fontWeight: 700 }}>
            {t("private_tour_eyebrow", { defaultValue: "Offre sur mesure" })}
          </span>
          <h1
            style={{
              position: "relative",
              margin: "14px 0 10px",
              fontFamily: "var(--g-font-display)",
              fontWeight: 800,
              fontSize: "clamp(24px, 3.6vw, 32px)",
              lineHeight: 1.2,
              color: "var(--g-burgundy)",
            }}
          >
            {loggedIn
              ? t("private_tour_denied_title", { defaultValue: "Ce produit n’est pas disponible pour votre compte" })
              : t("private_tour_guest_title", { defaultValue: "Ce produit est réservé à un client" })}
          </h1>
          <p style={{ position: "relative", margin: "0 auto 28px", maxWidth: 500, fontSize: 16, lineHeight: 1.6, color: "var(--g-ink-500)" }}>
            {loggedIn
              ? t("private_tour_denied_text", {
                  defaultValue:
                    "Il a été conçu sur mesure pour une autre agence. Les produits préparés pour vous se trouvent dans votre offre personnalisée.",
                })
              : t("private_tour_guest_text", {
                  defaultValue:
                    "Il a été conçu sur mesure pour une agence partenaire. Connectez-vous avec le compte concerné pour le consulter et le réserver.",
                })}
          </p>
          <div style={{ position: "relative", display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            {loggedIn ? (
              <Link href={`/${lang}/${currency}/favourite-tours` as Route} className="gdx-btn primary">
                {t("private_tour_my_offer", { defaultValue: "Voir mon offre personnalisée" })}
              </Link>
            ) : (
              <Link
                href={`/${lang}/${currency}/login${next ? `?next=${encodeURIComponent(next)}` : ""}` as Route}
                className="gdx-btn primary"
              >
                {t("private_tour_login", { defaultValue: "Se connecter" })}
              </Link>
            )}
            <Link
              href={`/${lang}/${currency}/tours` as Route}
              className="gdx-btn"
              style={{ background: "#FFFFFF", color: "var(--g-burgundy)", border: "1.5px solid var(--g-cream-deep)" }}
            >
              {t("private_tour_browse", { defaultValue: "Découvrir nos visites" })}
            </Link>
          </div>
          <p
            style={{
              position: "relative",
              margin: "26px 0 0",
              paddingTop: 18,
              borderTop: "1px solid var(--g-line)",
              fontSize: 13,
              color: "var(--g-ink-500)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--g-green-ada)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            {t("private_tour_footnote", {
              defaultValue: "Les offres sur mesure ne sont visibles que par l’agence concernée et son réseau.",
            })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <TourPageView
      lang={lang}
      currency={currency}
      tour={state.tour}
      city={state.city}
      galleries={state.galleries}
      continents={state.continents}
      t={t as any}
    />
  );
};

export default PrivateTourView;
